import { useEffect } from 'react';
import { Product } from '@/mocks/products';

interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}

const categoryConfig: Record<string, { color: string }> = {
  strategic: { color: '#FF6B35' },
  foundation: { color: '#00D4FF' },
  marketing: { color: '#A855F7' },
  fishery: { color: '#10B981' },
};

function openProductLink(link: { label: string; url?: string }) {
  if (!link?.url) return;
  if (link.url.startsWith('wechat://')) {
    const command = link.url.replace('wechat://', '请复制微信口令访问：');
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(command);
    }
    alert('小程序访问口令已复制到剪贴板');
    return;
  }
  window.open(link.url, '_blank', 'noopener,noreferrer');
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  useEffect(() => {
    if (!product) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [product, onClose]);

  if (!product) return null;

  const color = categoryConfig[product.category]?.color || '#00D4FF';
  const links = product.links?.length
    ? product.links
    : product.url
      ? [{ label: '立即访问', url: product.url }]
      : [];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex md:items-center md:justify-center animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="max-w-2xl w-full h-[100dvh] md:h-auto md:max-h-[86vh] overflow-y-auto md:rounded-[32px] rounded-none border p-6 md:p-8 shadow-[0_0_60px_rgba(0,212,255,0.12)] animate-scaleIn"
        style={{
          backgroundColor: '#0a1628',
          borderColor: `${color}20`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 手机端顶部关闭栏 */}
        <div className="md:hidden flex items-center justify-between mb-4 -mx-2 px-2 pt-2">
          <span className="text-sm text-white/50 font-medium">产品详情</span>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${color}20` }}
            >
              <i className={`${product.icon} text-2xl`} style={{ color }}></i>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white">{product.name}</div>
              <div className="mt-1" style={{ color }}>{product.slogan}</div>
            </div>
          </div>
          {/* PC端关闭按钮 */}
          <button
            onClick={onClose}
            className="hidden md:block text-white/50 hover:text-white text-3xl transition-colors cursor-pointer leading-none"
          >
            ×
          </button>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {product.tags?.map((tag) => (
            <span
              key={tag}
              className="rounded-full px-3 py-1 text-xs text-white/65 border"
              style={{
                backgroundColor: `${color}10`,
                borderColor: `${color}15`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* 信息网格 */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-sm mb-2" style={{ color }}>所属层级</div>
            <div className="text-white/75">{product.layer}</div>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-sm mb-2" style={{ color }}>适用对象</div>
            <div className="text-white/75">{product.audience || '待定'}</div>
          </div>
        </div>

        {/* 核心亮点 */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6">
          <div className="text-sm mb-3" style={{ color }}>核心亮点</div>
          <div className="space-y-3">
            {product.highlights.map((point) => (
              <div key={point} className="flex gap-3 text-white/70">
                <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 价值描述 */}
        {product.value && (
          <div className="text-white/70 text-base md:text-lg leading-8 mb-8">{product.value}</div>
        )}

        {/* 访问入口 */}
        {links.length > 0 && (
          <>
            <div className="text-sm mb-3" style={{ color }}>访问入口</div>
            <div className="flex flex-wrap gap-4">
              {links.map((link) =>
                link.url ? (
                  <button
                    key={link.label}
                    onClick={() => openProductLink(link)}
                    className="px-5 py-3 rounded-2xl border bg-white/5 hover:bg-white/10 transition-colors cursor-pointer text-sm text-white/80"
                    style={{ borderColor: `${color}30` }}
                  >
                    {link.label}
                  </button>
                ) : (
                  <span
                    key={link.label}
                    className="px-5 py-3 rounded-2xl border border-white/10 bg-white/5 text-white/45 text-sm"
                  >
                    {link.label}
                  </span>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}